<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email as emailValidator } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { maskCpf, maskCep } from 'dashboard/helper/inputMasks';
import PhoneInput from 'dashboard/components/widgets/forms/PhoneInput.vue';
import TeamsAPI from 'dashboard/api/teams';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
  accessLocked: {
    type: Boolean,
    default: false,
  },
  isAccountOwner: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentEmail = ref(props.email);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = computed(() => ({ email: agentEmail.value }));

// Extended fields from custom_attributes
const ca = props.customAttributes || {};
const phoneNumber = ref(ca.phone_number || '');
const phoneDialCode = ref('');
const address = ref(ca.address || '');
const addressComplement = ref(ca.address_complement || '');
const city = ref(ca.city || '');
const agentState = ref(ca.state || '');
const zipCode = ref(ca.zip_code || '');
const rg = ref(ca.rg || '');
const cpf = ref(ca.cpf || '');
const managerName = ref(ca.manager_name || '');
const agentAccessLocked = ref(props.accessLocked);

// Teams
const allTeams = ref([]);
const selectedTeamIds = ref([]);
const initialTeamIds = ref([]);
const isLoadingTeams = ref(false);

const rules = {
  agentName: { required, minLength: minLength(1) },
  agentEmail: { required, email: emailValidator },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const onPhoneInput = value => {
  phoneNumber.value = value;
};
const onPhoneSetCode = code => {
  phoneDialCode.value = code;
};
const applyCpfMask = () => {
  cpf.value = maskCpf(cpf.value);
};
const applyCepMask = () => {
  zipCode.value = maskCep(zipCode.value);
};

const toggleTeam = teamId => {
  const idx = selectedTeamIds.value.indexOf(teamId);
  if (idx > -1) {
    selectedTeamIds.value.splice(idx, 1);
  } else {
    selectedTeamIds.value.push(teamId);
  }
};

// Fetch all teams and determine which ones this agent belongs to
const loadTeams = async () => {
  isLoadingTeams.value = true;
  try {
    const { data: teams } = await TeamsAPI.get(true);
    allTeams.value = teams || [];

    // For each team, check if this agent is a member
    const memberChecks = teams.map(async team => {
      try {
        const { data: members } = await TeamsAPI.getAgents({
          teamId: team.id,
        });
        if (members.some(m => m.id === props.id)) {
          return team.id;
        }
      } catch {
        // Team member fetch failed — skip
      }
      return null;
    });

    const results = await Promise.all(memberChecks);
    const ids = results.filter(Boolean);
    selectedTeamIds.value = [...ids];
    initialTeamIds.value = [...ids];
  } catch {
    // Teams fetch failed — leave empty
  } finally {
    isLoadingTeams.value = false;
  }
};

// Sync team membership changes
const syncTeams = async () => {
  const added = selectedTeamIds.value.filter(
    id => !initialTeamIds.value.includes(id)
  );
  const removed = initialTeamIds.value.filter(
    id => !selectedTeamIds.value.includes(id)
  );

  // Add agent to newly selected teams
  const addPromises = added.map(teamId =>
    TeamsAPI.addAgents({ teamId, agentsList: [props.id] })
  );

  // Remove agent from deselected teams — atomic DELETE (no read-then-write)
  const removePromises = removed.map(teamId =>
    TeamsAPI.removeAgents({ teamId, agentsList: [props.id] })
  );

  await Promise.all([...addPromises, ...removePromises]);
};

onMounted(() => {
  loadTeams();
});

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      email: agentEmail.value,
      availability: agentAvailability.value,
      custom_attributes: {
        // PhoneInput does NOT emit 'setCode' on mount — only on user interaction.
        // When phoneDialCode is empty, phoneNumber.value already holds the
        // full E.164 value ('+ 5511988552211') from the DB.
        phone_number: phoneDialCode.value
          ? `${phoneDialCode.value}${phoneNumber.value}`
          : phoneNumber.value || '',
        address: address.value,
        address_complement: addressComplement.value,
        city: city.value,
        state: agentState.value,
        zip_code: zipCode.value,
        rg: rg.value,
        cpf: cpf.value,
        manager_name: managerName.value,
      },
    };

    // Include access_locked only if not account owner
    if (!props.isAccountOwner) {
      payload.access_locked = agentAccessLocked.value;
    }

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);

    // Sync team membership if changed
    const teamsChanged =
      selectedTeamIds.value.length !== initialTeamIds.value.length ||
      selectedTeamIds.value.some(id => !initialTeamIds.value.includes(id));
    if (teamsChanged) {
      await syncTeams();
    }

    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
          <span v-if="v$.agentEmail.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.EMAIL.ERROR') }}
          </span>
          <p class="text-xs text-n-slate-10 mt-0.5">
            {{ $t('AGENT_MGMT.EDIT.FORM.EMAIL.HELP') }}
          </p>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Teams Assignment -->
      <div
        v-if="allTeams.length > 0"
        class="border-t border-n-weak pt-4 mt-2 mb-4"
      >
        <h3 class="text-sm font-medium text-n-slate-11 mb-3">
          {{ $t('AGENT_MGMT.EDIT.FORM.TEAMS.LABEL') }}
        </h3>
        <p class="text-xs text-n-slate-10 mb-2">
          {{ $t('AGENT_MGMT.EDIT.FORM.TEAMS.DESCRIPTION') }}
        </p>
        <div v-if="isLoadingTeams" class="text-xs text-n-slate-10 py-2">
          {{ $t('AGENT_MGMT.EDIT.FORM.TEAMS.LOADING') }}
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <label
            v-for="team in allTeams"
            :key="team.id"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm"
            :class="
              selectedTeamIds.includes(team.id)
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-800/20 text-woot-600 dark:text-woot-400'
                : 'border-n-weak hover:border-n-slate-6 text-n-slate-11'
            "
          >
            <input
              type="checkbox"
              class="h-3.5 w-3.5 rounded border-n-slate-6 text-woot-500 focus:ring-woot-500"
              :checked="selectedTeamIds.includes(team.id)"
              @change="toggleTeam(team.id)"
            />
            {{ team.name }}
          </label>
        </div>
      </div>

      <!-- Extended Profile Fields -->
      <div class="border-t border-n-weak pt-4 mt-2 mb-4">
        <h3 class="text-sm font-medium text-n-slate-11 mb-3">
          {{ $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.TITLE') }}
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{
                $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.PHONE.LABEL')
              }}
            </label>
            <PhoneInput
              :model-value="phoneNumber"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.PHONE.PLACEHOLDER'
                )
              "
              @update:model-value="onPhoneInput"
              @set-code="onPhoneSetCode"
            />
          </div>
          <label>
            {{ $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CPF.LABEL') }}
            <input
              v-model="cpf"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CPF.PLACEHOLDER'
                )
              "
              @input="applyCpfMask"
            />
          </label>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <label>
            {{ $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.RG.LABEL') }}
            <input
              v-model="rg"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.RG.PLACEHOLDER'
                )
              "
            />
          </label>
          <label>
            {{
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.MANAGER.LABEL')
            }}
            <input
              v-model="managerName"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.MANAGER.PLACEHOLDER'
                )
              "
            />
          </label>
        </div>

        <label class="mt-2">
          {{
            $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ADDRESS.LABEL')
          }}
          <input
            v-model="address"
            type="text"
            :placeholder="
              $t(
                'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ADDRESS.PLACEHOLDER'
              )
            "
          />
        </label>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <label>
            {{
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.COMPLEMENT.LABEL')
            }}
            <input
              v-model="addressComplement"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.COMPLEMENT.PLACEHOLDER'
                )
              "
            />
          </label>
          <label>
            {{
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CITY.LABEL')
            }}
            <input
              v-model="city"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.CITY.PLACEHOLDER'
                )
              "
            />
          </label>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <label>
            {{
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.STATE.LABEL')
            }}
            <input
              v-model="agentState"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.STATE.PLACEHOLDER'
                )
              "
            />
          </label>
          <label>
            {{
              $t('PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ZIP_CODE.LABEL')
            }}
            <input
              v-model="zipCode"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.EXTENDED_FIELDS.ZIP_CODE.PLACEHOLDER'
                )
              "
              @input="applyCepMask"
            />
          </label>
        </div>
      </div>

      <!-- Access Toggle -->
      <div
        class="flex items-center justify-between w-full px-0 py-3 mt-2 border-t border-n-weak"
      >
        <div>
          <span class="block text-sm font-medium">
            Acesso ao sistema
          </span>
          <span class="block text-xs text-n-slate-10">
            {{
              props.isAccountOwner
                ? 'O dono da conta não pode ser desativado.'
                : agentAccessLocked
                  ? 'Este atendente está bloqueado e não pode fazer login.'
                  : 'Este atendente tem acesso ao sistema.'
            }}
          </span>
        </div>
        <button
          type="button"
          class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :class="[
            !agentAccessLocked ? 'bg-woot-500' : 'bg-n-slate-6',
            props.isAccountOwner ? 'opacity-50 cursor-not-allowed' : '',
          ]"
          :disabled="props.isAccountOwner"
          @click="agentAccessLocked = !agentAccessLocked"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            :class="
              !agentAccessLocked ? 'translate-x-5' : 'translate-x-0'
            "
          />
        </button>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="
              $t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')
            "
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
